import { useState } from "react";
import { isFullUrl, isVideoUrl } from "@/lib/media";
import { supabase } from "@/lib/supabase";

interface SmartMediaProps {
  src: string;
  alt: string;
  className?: string;
  videoControls?: boolean;
  /** true para imágenes visibles de inmediato (sin lazy loading). */
  priority?: boolean;
  sizes?: string;
}

const MEDIA_BUCKET = "covers";

/** Resuelve una ruta interna a su URL pública estable. */
function resolveMediaUrl(src: string): string {
  if (!src) return "";
  if (isFullUrl(src)) return src;
  return supabase.storage.from(MEDIA_BUCKET).getPublicUrl(src).data.publicUrl;
}

/**
 * Construye un srcset con las miniaturas derivadas (-thumb / -small) para
 * imágenes WebP internas. Para URLs externas o formatos sin miniaturas
 * devuelve null (se usa la imagen principal directamente).
 */
function buildResponsive(src: string): { srcSet: string; sizes: string } | null {
  if (!src || isFullUrl(src)) return null;
  if (!/\.webp$/i.test(src)) return null;
  const bucket = supabase.storage.from(MEDIA_BUCKET);
  const base = src.replace(/\.webp$/i, "");
  const main = bucket.getPublicUrl(src).data.publicUrl;
  const thumb = bucket.getPublicUrl(`${base}-thumb.webp`).data.publicUrl;
  const small = bucket.getPublicUrl(`${base}-small.webp`).data.publicUrl;
  return {
    srcSet: `${thumb} 400w, ${small} 800w, ${main} 1600w`,
    sizes: "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  };
}

/**
 * Renderiza una imagen o video resolviendo las rutas de almacenamiento a URLs
 * públicas. Aplica lazy loading, decodificación asíncrona y, cuando existen,
 * imágenes responsivas (srcset) con miniaturas WebP.
 */
export default function SmartMedia({
  src,
  alt,
  className,
  videoControls = true,
  priority = false,
  sizes,
}: SmartMediaProps) {
  const [srcsetFailed, setSrcsetFailed] = useState(false);
  const [broken, setBroken] = useState(false);

  const resolved = resolveMediaUrl(src);
  const isVideo = isVideoUrl(src);

  if (!resolved || broken) {
    return <div className={`${className ?? ""} bg-background-200`}></div>;
  }

  if (isVideo) {
    return (
      <video
        src={resolved}
        aria-label={alt}
        className={className}
        controls={videoControls}
        muted={!videoControls}
        playsInline
        preload="metadata"
      />
    );
  }

  const responsive = srcsetFailed ? null : buildResponsive(src);

  return (
    <img
      key={srcsetFailed ? "plain" : "responsive"}
      src={resolved}
      srcSet={responsive?.srcSet}
      sizes={sizes ?? responsive?.sizes}
      alt={alt}
      className={className}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      onError={() => {
        // Si falla una miniatura del srcset, reintenta solo con la principal;
        // si la principal también falla, muestra el placeholder.
        if (responsive) setSrcsetFailed(true);
        else setBroken(true);
      }}
    />
  );
}